import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Grow,
  useTheme,
  useMediaQuery,
  Stack,
  Tooltip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  WarningAmber as WarningIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CalendarToday as DateIcon,
  AttachMoney as MoneyIcon,
  Work as ContractsIcon,
  EventNote as SessionsIcon,
  TrendingUp as EarningsIcon,
  VideoCall as TelehealthIcon,
  Person as InPersonIcon,
  BusinessCenter as HybridIcon,
  Psychology as SpecializationIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
  Wc as GenderIcon,
  ChildCare as AgeIcon,
  LocalHospital as MedicalIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  AttachFile as AttachmentIcon,
  Label as TagIcon,
  Article as ReportIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  InsertDriveFile as DocumentIcon,
  FilterList as FilterIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import {
  FileAttachment,
  ServicePackageListItem,
} from '../../types/servicePackage'
import { useNavigate } from 'react-router-dom'
import { servicePackageService } from '../../services/servicePackageService'
import { reportTypes, bodyRegions } from '../../data/servicePackageMockData'
import AdvancedPagination from '../pagination/AdvancedPagination'
import type { TablePaginationProps } from '@mui/material'

interface ServicePackageListProps {
  servicePackages: ServicePackageListItem[]
  onRefresh?: () => Promise<void> | void
  totalRecords?: number // Total number of packages (before filtering)
  activeTab?: 'all' | 'published' | 'draft' | 'archived' // Current active tab
  hasFilters?: boolean // Whether any filters are applied (search, specialization)
  page?: number // Current page (0-indexed for TablePagination)
  rowsPerPage?: number // Number of rows per page
  onPageChange?: TablePaginationProps['onPageChange']
  onRowsPerPageChange?: TablePaginationProps['onRowsPerPageChange']
  providerId?: string // Optional provider ID for filtering packages
}

export const AgencyServicePackageList: React.FC<ServicePackageListProps> = ({
  servicePackages,
  onRefresh,
  totalRecords = 0,
  activeTab = 'all',
  hasFilters = false,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  providerId,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  )
  const [dialogState, setDialogState] = useState<{
    type: 'delete' | 'visibility' | null
    pkg: ServicePackageListItem | null
  }>({ type: null, pkg: null })
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [selectedPackageForDetails, setSelectedPackageForDetails] =
    useState<ServicePackageListItem | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  } | null>(null)

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity })
  }

  const handleSnackbarClose = () => {
    setSnackbar(null)
  }

  const handleDeleteClick = (pkg: ServicePackageListItem) => {
    setDialogState({ type: 'delete', pkg })
  }

  const handleVisibilityClick = (pkg: ServicePackageListItem) => {
    setDialogState({ type: 'visibility', pkg })
  }

  const handleDialogClose = () => {
    if (actionLoading) return
    setDialogState({ type: null, pkg: null })
  }

  const handleConfirmAction = async () => {
    const currentType = dialogState.type
    const currentPackage = dialogState.pkg

    if (!currentType || !currentPackage) return

    setActionLoading(true)

    try {
      if (currentType === 'delete') {
        await servicePackageService.delete(currentPackage.id)
        showSnackbar('Service package deleted successfully.', 'success')
      }

      if (currentType === 'visibility') {
        const nextValue = !currentPackage.acceptInsuranceInSearch
        await servicePackageService.updateVisibility(
          currentPackage.id,
          nextValue
        )
        showSnackbar(
          nextValue
            ? 'This service package is now visible in patient search results.'
            : 'This service package is now hidden from patient search results.',
          'success'
        )
      }

      setDialogState({ type: null, pkg: null })

      if (onRefresh) {
        await onRefresh()
      }
    } catch (error) {
      console.error('Failed to perform service package action:', error)
      showSnackbar(
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.',
        'error'
      )
    } finally {
      setActionLoading(false)
    }
  }

  // console.log("servicePackages",servicePackages)

  const dialogOpen = Boolean(dialogState.type && dialogState.pkg)
  const pendingVisibilityValue =
    dialogState.type === 'visibility' && dialogState.pkg
      ? !dialogState.pkg.acceptInsuranceInSearch
      : undefined
  const dialogPackageTitle = dialogState.pkg?.serviceTitle ?? ''
  const dialogTitle =
    dialogState.type === 'delete'
      ? 'Delete this service package?'
      : dialogState.type === 'visibility'
        ? pendingVisibilityValue
          ? 'Make this package visible to patients?'
          : 'Hide this package from patients?'
        : ''
  const confirmLabel =
    dialogState.type === 'delete'
      ? 'Delete Package'
      : dialogState.type === 'visibility'
        ? pendingVisibilityValue
          ? 'Show Package'
          : 'Hide Package'
        : ''

  const getFileNameFromUrl = (url?: string) => {
    if (!url) return 'Document'
    try {
      // Remove query parameters
      const urlWithoutQuery = url.split('?')[0]

      // Try to parse as URL first
      try {
        const urlObj = new URL(urlWithoutQuery)
        const pathname = urlObj.pathname
        const segments = pathname.split('/').filter(Boolean)
        const filename = segments[segments.length - 1] || 'Document'
        return decodeURIComponent(filename)
      } catch {
        // If URL parsing fails, try direct path extraction
        const segments = urlWithoutQuery.split('/').filter(Boolean)
        const filename = segments[segments.length - 1] || 'Document'
        return decodeURIComponent(filename)
      }
    } catch {
      return 'Document'
    }
  }

  const getAttachmentDisplay = (
    attachment: FileAttachment | string
  ): { name: string; url?: string } => {
    if (typeof attachment === 'string') {
      return {
        name: getFileNameFromUrl(attachment),
        url: attachment,
      }
    }

    return {
      name: attachment.name || getFileNameFromUrl(attachment.url),
      url: attachment.url,
    }
  }

  const getModalityIcon = (modality: string) => {
    switch (modality) {
      case 'in_person':
        return <InPersonIcon sx={{ fontSize: 16 }} />
      case 'telehealth':
        return <TelehealthIcon sx={{ fontSize: 16 }} />
      case 'hybrid':
        return <HybridIcon sx={{ fontSize: 16 }} />
      default:
        return null
    }
  }

  const getModalityLabel = (modality: string) => {
    switch (modality) {
      case 'in_person':
        return 'In-Person'
      case 'telehealth':
        return 'Telehealth'
      case 'hybrid':
        return 'Hybrid'
      default:
        return modality
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)

    if (Number.isNaN(date.getTime())) {
      return 'Not available'
    }

    const datePart = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date)

    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      // timeZoneName: 'short',
    }).format(date)

    return `${datePart} • ${timePart}`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success'
      case 'draft':
        return 'warning'
      case 'archived':
        return 'error'
      default:
        return 'default'
    }
  }

  const handleCreateNew = () => {
    navigate(`/service-packages/agency/new/${providerId}`)
  }

  const handleEdit = (pkg: ServicePackageListItem) => {
    if (!pkg.formData) {
      showSnackbar(
        'Full package details are unavailable. Please refresh the list and try again.',
        'error'
      )
      return
    }

    console.log('pkgpkg', pkg)

    navigate(`/agency-service-packages/edit/${pkg.id}`, {
      state: {
        initialData: pkg.formData,
        servicePackageId: pkg.id,
        providerId: providerId || pkg.created_by || (pkg.formData as any)?.therapist_id,
      },
    })
  }

  const handleViewDetails = (pkg: ServicePackageListItem) => {
    setSelectedPackageForDetails(pkg)
    setDetailsDialogOpen(true)
  }

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false)
    setTimeout(() => setSelectedPackageForDetails(null), 200)
  }

  // Determine placeholder type
  // No packages at all: only when on 'all' tab with no filters and totalRecords is 0
  const hasNoPackagesAtAll =
    totalRecords === 0 && !hasFilters && activeTab === 'all'
  // No packages for filters: when filters are applied but no results
  const hasNoPackagesForFilters = servicePackages.length === 0 && hasFilters
  // No packages for specific tab: when on a specific tab (not 'all') with no filters and no results
  const hasNoPackagesForTab =
    servicePackages.length === 0 && !hasFilters && activeTab !== 'all'

  // Get tab-specific messages
  const getTabMessage = () => {
    switch (activeTab) {
      case 'published':
        return {
          title: 'No Published Packages',
          description:
            "You don't have any published service packages yet. Create and publish your first package to make it visible to patients.",
        }
      case 'draft':
        return {
          title: 'No Draft Packages',
          description:
            "You don't have any draft service packages. Create a new package and save it as a draft to work on it later.",
        }
      case 'archived':
        return {
          title: 'No Archived Packages',
          description: "You don't have any archived service packages.",
        }
      default:
        return {
          title: 'No Service Packages Yet',
          description:
            'Create your first service package to start offering services to patients. Define your pricing, specialties, and availability.',
        }
    }
  }

  return (
    <>
      {/* Only show Create New Package button if we have packages or filters applied */}
      {!hasNoPackagesAtAll && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mb: 2,
            pb: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: theme.shadows[4],
              '&:hover': {
                boxShadow: theme.shadows[8],
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Create New Package
          </Button>
        </Box>
      )}

      {servicePackages.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          {hasNoPackagesAtAll ? (
            // No packages at all - show full onboarding message
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <AddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              <Typography
                variant="h5"
                color="text.primary"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                No Service Packages Yet
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}
              >
                Create your first service package to start offering services to
                patients. Define your pricing, specialties, and availability.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    boxShadow: theme.shadows[8],
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Create Your First Package
              </Button>
            </>
          ) : hasNoPackagesForFilters ? (
            // No packages matching filters
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <FilterIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
              <Typography
                variant="h5"
                color="text.primary"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                No Packages Match Your Filters
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}
              >
                Try adjusting your search query or specialization filter to see
                more results. You can also clear all filters to view all your
                service packages.
              </Typography>
            </>
          ) : (
            // No packages for specific tab
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <InfoIcon sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
              <Typography
                variant="h5"
                color="text.primary"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                {getTabMessage().title}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}
              >
                {getTabMessage().description}
              </Typography>
            </>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {servicePackages.map(servicePackage => {
            const statsCards = [
              {
                label: 'Total Contracts',
                value: (servicePackage.totalContracts ?? 0).toLocaleString(),
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.12
                )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                borderColor: alpha(theme.palette.primary.main, 0.18),
                iconBg: alpha(theme.palette.primary.main, 0.14),
                icon: <ContractsIcon sx={{ fontSize: 20 }} />,
                valueColor: 'primary.main' as const,
              },
              {
                label: 'Total Sessions',
                value: (servicePackage.totalSessions ?? 0).toLocaleString(),
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.secondary.main,
                  0.12
                )} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
                borderColor: alpha(theme.palette.secondary.main, 0.18),
                iconBg: alpha(theme.palette.secondary.main, 0.14),
                icon: <SessionsIcon sx={{ fontSize: 20 }} />,
                valueColor: 'secondary.main' as const,
              },
              {
                label: 'Total Earnings',
                value: `$${(Number(servicePackage?.totalEarnings) || 0).toFixed(2)}`,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.success.main,
                  0.12
                )} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`,
                borderColor: alpha(theme.palette.success.main, 0.18),
                iconBg: alpha(theme.palette.success.main, 0.14),
                icon: <EarningsIcon sx={{ fontSize: 20 }} />,
                valueColor: 'success.main' as const,
              },
            ]

            const isVisibleInSearch = servicePackage.acceptInsuranceInSearch


            console.log("servicePackage", servicePackage)

            return (
              <Grid item xs={12} key={servicePackage.id}>
                <Grow in={true} timeout={300}>
                  <Card
                    sx={{
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8],
                      },
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    {/* View Details Button */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '13px',
                          marginRight: 1,
                        }}
                      >
                        Created by : {servicePackage.created_by}
                      </Typography>
                      <Tooltip title="View Full Details" arrow placement="top">
                        <IconButton
                          size="small"
                          // onClick={() => handleViewDetails(servicePackage)}
                          onClick={() => console.log('View details for package:', servicePackage)}
                          sx={{
                            color: 'info.main',
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.info.main, 0.15),
                              borderColor: 'info.main',
                            },
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      {/* Header Section */}
                      <Box sx={{ mb: 3, pr: isMobile ? 0 : 12 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 1.25,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              lineHeight: 1.3,
                            }}
                          >
                            {servicePackage.serviceTitle}
                          </Typography>
                          <Chip
                            label={servicePackage.status.toUpperCase()}
                            size="small"
                            color={getStatusColor(servicePackage.status) as any}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                            mb: 2,
                          }}
                        >
                          {servicePackage.shortDescription}
                        </Typography>
                      </Box>

                      {/* Key Info Grid */}
                      <Grid container spacing={2} sx={{ mb: 2.5 }}>
                        {/* Pricing Card */}
                        <Grid item xs={12} sm={6} md={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              background: `linear-gradient(135deg, ${alpha(
                                theme.palette.primary.main,
                                0.1
                              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 0.5,
                              }}
                            >
                              <MoneyIcon
                                sx={{
                                  fontSize: 16,
                                  color: 'primary.main',
                                  mr: 0.5,
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                Rate
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              color="primary.main"
                              sx={{ fontWeight: 700 }}
                            >
                              {servicePackage.privatePayRate
                                ? Number(servicePackage.privatePayRate).toFixed(
                                  2
                                )
                                : 'N/A'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              per session
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Duration Card */}
                        <Grid item xs={12} sm={6} md={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              background: `linear-gradient(135deg, ${alpha(
                                theme.palette.secondary.main,
                                0.1
                              )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 0.5,
                              }}
                            >
                              <TimeIcon
                                sx={{
                                  fontSize: 16,
                                  color: 'secondary.main',
                                  mr: 0.5,
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                Duration
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              color="secondary.main"
                              sx={{ fontWeight: 700 }}
                            >
                              {servicePackage.sessionLengthMin
                                ? `${servicePackage.sessionLengthMin} mins`
                                : 'N/A'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Typical session length
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Search Visibility Card */}
                        <Grid item xs={12} sm={12} md={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              background: `linear-gradient(135deg, ${alpha(
                                theme.palette.success.main,
                                0.1
                              )} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`,
                              border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 0.5,
                              }}
                            >
                              {isVisibleInSearch ? (
                                <VisibilityIcon
                                  sx={{
                                    fontSize: 16,
                                    color: 'success.main',
                                    mr: 0.5,
                                  }}
                                />
                              ) : (
                                <VisibilityOffIcon
                                  sx={{
                                    fontSize: 16,
                                    color: 'success.main',
                                    mr: 0.5,
                                  }}
                                />
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                Search Visibility
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              color="success.main"
                              sx={{ fontWeight: 700 }}
                            >
                              {isVisibleInSearch
                                ? 'Visible to patients'
                                : 'Hidden from patients'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {isVisibleInSearch
                                ? 'Patients can discover this package in search.'
                                : 'Patients cannot see this package until enabled.'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Box
                        sx={{
                          mt: 1,
                          p: { xs: 2, md: 2.5 },
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={{ xs: 2, md: 3 }}
                          alignItems="stretch"
                          justifyContent="center"
                          sx={{
                            background: `linear-gradient(135deg, ${alpha(
                              theme.palette.primary.main,
                              0.04
                            )} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                            borderRadius: 3,
                            p: { xs: 2, md: 2.5 },
                            boxShadow: `0 8px 24px ${alpha(
                              theme.palette.common.black,
                              0.04
                            )}`,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="center"
                            textAlign={{ xs: 'left', md: 'center' }}
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.12
                                ),
                                color: 'primary.main',
                              }}
                            >
                              <SpecializationIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography
                                variant="overline"
                                sx={{
                                  letterSpacing: 0.6,
                                  color: 'text.secondary',
                                }}
                              >
                                Specialization
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                {servicePackage?.specializationName ||
                                  'Not specified'}
                              </Typography>
                            </Box>
                          </Stack>

                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                              display: { xs: 'none', md: 'block' },
                              opacity: 0.3,
                            }}
                          />

                          <Stack
                            spacing={1.5}
                            justifyContent="center"
                            textAlign={{ xs: 'left', md: 'center' }}
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Typography
                              variant="overline"
                              sx={{
                                letterSpacing: 0.6,
                                color: 'text.secondary',
                              }}
                            >
                              Modalities Offered
                            </Typography>
                            {servicePackage.modality.length > 0 ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                justifyContent={{
                                  xs: 'flex-start',
                                  md: 'center',
                                }}
                              >
                                {servicePackage.modality.map(mode => (
                                  <Chip
                                    key={mode}
                                    icon={getModalityIcon(mode)}
                                    label={getModalityLabel(mode)}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(
                                        theme.palette.secondary.main,
                                        0.12
                                      ),
                                      color: 'secondary.main',
                                      fontWeight: 600,
                                      border: 'none',
                                    }}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No modalities listed.
                              </Typography>
                            )}
                          </Stack>

                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                              display: { xs: 'none', md: 'block' },
                              opacity: 0.3,
                            }}
                          />

                          <Stack
                            spacing={0.75}
                            justifyContent="center"
                            textAlign={{ xs: 'left', md: 'center' }}
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent={{
                                xs: 'flex-start',
                                md: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: alpha(
                                    theme.palette.info.main,
                                    0.14
                                  ),
                                  color: 'info.main',
                                }}
                              >
                                <DateIcon sx={{ fontSize: 18 }} />
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Created{' '}
                                {formatDateTime(servicePackage.createdAt)}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent={{
                                xs: 'flex-start',
                                md: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: alpha(
                                    theme.palette.secondary.main,
                                    0.14
                                  ),
                                  color: 'secondary.main',
                                }}
                              >
                                <TimeIcon sx={{ fontSize: 18 }} />
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Updated{' '}
                                {formatDateTime(servicePackage.updatedAt)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>

                      {/* Expanded Details */}
                      <Collapse
                        in={expandedItems[servicePackage.id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Divider sx={{ my: 2.5 }} />

                        <Stack spacing={3}>
                          <Box>
                            <Typography
                              variant="overline"
                              color="text.secondary"
                              sx={{ letterSpacing: 0.8 }}
                            >
                              Performance Snapshot
                            </Typography>
                          </Box>
                          <Grid container spacing={2}>
                            {statsCards.map(card => (
                              <Grid item xs={12} sm={4} key={card.label}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    background: card.background,
                                    border: `1px solid ${card.borderColor}`,
                                    boxShadow: `0 14px 35px ${alpha(
                                      theme.palette.common.black,
                                      0.05
                                    )}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    height: '100%',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: card.iconBg,
                                        color: card.valueColor,
                                      }}
                                    >
                                      {card.icon}
                                    </Box>
                                    <Typography
                                      variant="subtitle2"
                                      color="text.secondary"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {card.label}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: 700,
                                      color: card.valueColor,
                                      letterSpacing: '-0.01em',
                                    }}
                                  >
                                    {card.value}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Stack>
                      </Collapse>
                    </CardContent>

                    {/* Actions Footer */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 3,
                        py: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          size="small"
                          onClick={() => toggleExpand(servicePackage.id)}
                          endIcon={
                            expandedItems[servicePackage.id] ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )
                          }
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            color: 'text.secondary',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                          }}
                        >
                          {expandedItems[servicePackage.id]
                            ? 'Show Less'
                            : 'Show More'}
                        </Button>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip placement="top" title="Edit Package" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(servicePackage)}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>




                        <Tooltip placement="top" title="Delete Package" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(servicePackage)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ mx: 0.5 }}
                        />
                        <Tooltip placement="top" title={
                          isVisibleInSearch
                            ? 'Visible in patient search'
                            : 'Hidden from patient search'
                        }
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleVisibilityClick(servicePackage)
                            }
                            sx={{
                              color: isVisibleInSearch
                                ? 'success.main'
                                : 'warning.main',
                              '&:hover': {
                                bgcolor: alpha(
                                  isVisibleInSearch
                                    ? theme.palette.success.main
                                    : theme.palette.warning.main,
                                  0.1
                                ),
                              },
                            }}
                          >
                            {isVisibleInSearch ? (
                              <VisibilityIcon fontSize="small" />
                            ) : (
                              <VisibilityOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Card>
                </Grow>
              </Grid>
            )
          })}
        </Grid>
      )}

      {/* Pagination */}
      {onPageChange && onRowsPerPageChange && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            mt: 4,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <AdvancedPagination
            count={totalRecords || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Packages per page:"
          />
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="service-package-action-dialog"
      >
        <DialogTitle
          id="service-package-action-dialog"
          sx={{ fontWeight: 700, pb: 1 }}
        >
          {dialogTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {dialogState.type === 'delete' ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {`Remove "${dialogPackageTitle || 'this service package'}" from your offerings?`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Deleting this package permanently removes it from patient search
                and booking.
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <WarningIcon sx={{ color: 'error.main', fontSize: 22 }} />
                <Typography variant="body2" color="error.main">
                  This action cannot be undone.
                </Typography>
              </Box>
            </Stack>
          ) : dialogState.type === 'visibility' ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {pendingVisibilityValue
                  ? 'Patients will be able to discover this package, explore its offerings, and book it.'
                  : 'Patients won’t be able to discover or view this package’s offerings, and they won’t be able to book it.'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can {pendingVisibilityValue ? 'hide it' : 'make it visible'}{' '}
                again anytime.
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button onClick={handleDialogClose} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={dialogState.type === 'delete' ? 'error' : 'primary'}
            disabled={actionLoading}
            sx={{ minWidth: 160, fontWeight: 600 }}
          >
            {actionLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Package Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.08
            )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
              }}
            >
              <InfoIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Service Package Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Complete information about this service
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseDetailsDialog}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedPackageForDetails && selectedPackageForDetails.formData ? (
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Header Section */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}
                        >
                          {
                            selectedPackageForDetails.formData.basics
                              .serviceTitle
                          }
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          {
                            selectedPackageForDetails.formData.basics
                              .shortDescription
                          }
                        </Typography>
                      </Box>
                      <Chip
                        label={selectedPackageForDetails.status.toUpperCase()}
                        size="small"
                        color={
                          getStatusColor(
                            selectedPackageForDetails.status
                          ) as any
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Stack>
                </Box>

                {/* Long Description */}
                {selectedPackageForDetails.formData.basics
                  .detailedDescription && (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <DescriptionIcon
                          sx={{ fontSize: 20, color: 'primary.main' }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          Detailed Description
                        </Typography>
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          bgcolor: alpha(theme.palette.background.paper, 0.6),
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}
                        >
                          {
                            selectedPackageForDetails.formData.basics
                              .detailedDescription
                          }
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                {/* Basic Information Grid */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
                  >
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Age Range */}
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: alpha(theme.palette.info.main, 0.04),
                          borderColor: alpha(theme.palette.info.main, 0.2),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <AgeIcon sx={{ fontSize: 18, color: 'info.main' }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Age Range
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: 'info.main' }}
                        >
                          {selectedPackageForDetails.formData.basics.ageRange
                            .min || 'Any'}{' '}
                          -{' '}
                          {selectedPackageForDetails.formData.basics.ageRange
                            .max || 'Any'}{' '}
                          years
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Target Gender */}
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: alpha(theme.palette.secondary.main, 0.04),
                          borderColor: alpha(theme.palette.secondary.main, 0.2),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <GenderIcon
                            sx={{ fontSize: 18, color: 'secondary.main' }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Target Gender
                          </Typography>
                        </Box>
                        {(() => {
                          const targetGender: string[] | string | undefined =
                            selectedPackageForDetails.formData.basics
                              .targetGender as string[] | string | undefined
                          // Handle both array and string formats (for backward compatibility)
                          const genderArray = Array.isArray(targetGender)
                            ? targetGender
                            : typeof targetGender === 'string' && targetGender
                              ? targetGender
                                .split(/,\s*/)
                                .map(g => g.trim())
                                .filter(Boolean)
                              : []

                          return genderArray.length > 0 ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {genderArray.map((gender, idx) => (
                                <Chip
                                  key={idx}
                                  label={gender}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.secondary.main,
                                      0.12
                                    ),
                                    color: 'secondary.main',
                                    fontWeight: 600,
                                    mb: 1,
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              All
                            </Typography>
                          )
                        })()}
                      </Paper>
                    </Grid>

                    {/* Languages */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.success.main, 0.04),
                          borderColor: alpha(theme.palette.success.main, 0.2),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                        >
                          <LanguageIcon
                            sx={{ fontSize: 18, color: 'success.main' }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Languages Offered
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {selectedPackageForDetails.formData.basics.languages
                            .length > 0 ? (
                            selectedPackageForDetails.formData.basics.languages.map(
                              (lang, idx) => (
                                <Chip
                                  key={idx}
                                  label={lang}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.12
                                    ),
                                    color: 'success.main',
                                    fontWeight: 600,
                                    mb: 1,
                                  }}
                                />
                              )
                            )
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not specified
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Modalities */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                        >
                          <TelehealthIcon
                            sx={{ fontSize: 18, color: 'primary.main' }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Service Modalities
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {selectedPackageForDetails.formData.basics.modality
                            .length > 0 ? (
                            selectedPackageForDetails.formData.basics.modality.map(
                              (mode, idx) => (
                                <Chip
                                  key={idx}
                                  icon={getModalityIcon(mode)}
                                  label={getModalityLabel(mode)}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.12
                                    ),
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    mb: 1,
                                  }}
                                />
                              )
                            )
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not specified
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>

                {/* Clinical Coding */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <MedicalIcon sx={{ fontSize: 20, color: 'error.main' }} />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      Clinical Coding
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    {/* CPT Codes */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.error.main, 0.04),
                        borderColor: alpha(theme.palette.error.main, 0.2),
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}
                      >
                        CPT Codes
                      </Typography>
                      {selectedPackageForDetails.formData.clinicalCoding
                        .cptCodes.length > 0 ? (
                        <Stack spacing={1}>
                          {selectedPackageForDetails.formData.clinicalCoding.cptCodes.map(
                            (cpt, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1.5,
                                  borderRadius: 1,
                                  bgcolor: alpha(
                                    theme.palette.background.paper,
                                    0.8
                                  ),
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {cpt.code}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {cpt.description}
                                  </Typography>
                                </Box>
                              </Box>
                            )
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No CPT codes specified
                        </Typography>
                      )}
                    </Paper>

                    {/* ICD-10 Codes */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.04),
                        borderColor: alpha(theme.palette.warning.main, 0.2),
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}
                      >
                        ICD-10 Codes
                      </Typography>
                      {selectedPackageForDetails.formData.clinicalCoding
                        .icd10Codes.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {selectedPackageForDetails.formData.clinicalCoding.icd10Codes.map(
                            (icd, idx) => (
                              <Chip
                                key={idx}
                                label={`${icd.code} - ${icd.description}`}
                                size="small"
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.warning.main,
                                    0.12
                                  ),
                                  color: 'warning.main',
                                  fontWeight: 600,
                                  mb: 1,
                                }}
                              />
                            )
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No ICD-10 codes specified
                        </Typography>
                      )}
                    </Paper>

                    {/* Medical Necessity Note */}
                    {selectedPackageForDetails.formData.clinicalCoding
                      .medicalNecessityNote && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.info.main, 0.04),
                            borderColor: alpha(theme.palette.info.main, 0.2),
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                          >
                            Medical Necessity Note
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            {
                              selectedPackageForDetails.formData.clinicalCoding
                                .medicalNecessityNote
                            }
                          </Typography>
                        </Paper>
                      )}
                  </Stack>
                </Box>

                {/* Pricing & Billing */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PaymentIcon sx={{ fontSize: 20, color: 'success.main' }} />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      Pricing & Billing
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: alpha(theme.palette.success.main, 0.04),
                          borderColor: alpha(theme.palette.success.main, 0.2),
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                        >
                          Private Pay Rate
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: 'success.main' }}
                        >
                          $
                          {selectedPackageForDetails.formData.pricingBilling
                            .privatePayRate || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          per session
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: alpha(theme.palette.info.main, 0.04),
                          borderColor: alpha(theme.palette.info.main, 0.2),
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                        >
                          Insurance Acceptance
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {selectedPackageForDetails.formData.pricingBilling
                            .acceptInsurance ? (
                            <>
                              <CheckCircleIcon
                                sx={{ color: 'success.main', fontSize: 24 }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: 'success.main' }}
                              >
                                Accepts Insurance
                              </Typography>
                            </>
                          ) : (
                            <>
                              <CancelIcon
                                sx={{ color: 'error.main', fontSize: 24 }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: 'error.main' }}
                              >
                                Does Not Accept
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Insurance Payers */}
                    {selectedPackageForDetails.formData.pricingBilling
                      .acceptInsurance &&
                      selectedPackageForDetails.formData.pricingBilling
                        .acceptedPayers.length > 0 && (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.2
                              ),
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontWeight: 600,
                                mb: 1.5,
                                display: 'block',
                              }}
                            >
                              Accepted Insurance Providers
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {selectedPackageForDetails.formData.pricingBilling.acceptedPayers.map(
                                (payer, idx) => (
                                  <Chip
                                    key={idx}
                                    label={payer}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.12
                                      ),
                                      color: 'primary.main',
                                      fontWeight: 600,
                                      mb: 1,
                                    }}
                                  />
                                )
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      )}
                  </Grid>
                </Box>

                {/* Scheduling & Rules */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <ScheduleIcon
                      sx={{ fontSize: 20, color: 'secondary.main' }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      Scheduling & Service Area
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: alpha(theme.palette.secondary.main, 0.04),
                          borderColor: alpha(theme.palette.secondary.main, 0.2),
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                        >
                          Session Length
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: 'secondary.main' }}
                        >
                          {
                            selectedPackageForDetails.formData.schedulingRules
                              .sessionLengthMin
                          }{' '}
                          mins
                        </Typography>
                      </Paper>
                    </Grid>

                    {selectedPackageForDetails.formData.schedulingRules
                      .session_buffer != null && (
                        <Grid item xs={12} sm={6}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              height: '100%',
                              bgcolor: alpha(theme.palette.info.main, 0.04),
                              borderColor: alpha(theme.palette.info.main, 0.2),
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <TimeIcon
                                sx={{ fontSize: 18, color: 'info.main' }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Session Buffer
                              </Typography>
                            </Box>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, color: 'info.main' }}
                            >
                              {
                                selectedPackageForDetails.formData.schedulingRules
                                  .session_buffer
                              }{' '}
                              mins
                            </Typography>
                          </Paper>
                        </Grid>
                      )}

                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: alpha(theme.palette.info.main, 0.04),
                          borderColor: alpha(theme.palette.info.main, 0.2),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <LocationIcon
                            sx={{ fontSize: 18, color: 'info.main' }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Service Area
                          </Typography>
                        </Box>
                        {selectedPackageForDetails.formData.schedulingRules
                          .inPersonSettings.serviceArea === 'radius_miles' ? (
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: 'info.main' }}
                          >
                            {selectedPackageForDetails.formData.schedulingRules
                              .inPersonSettings.radiusMiles || 0}{' '}
                            miles radius
                          </Typography>
                        ) : (
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: 'info.main' }}
                          >
                            Fixed ZIP codes
                          </Typography>
                        )}
                      </Paper>
                    </Grid>

                    {/* ZIP Codes if applicable */}
                    {selectedPackageForDetails.formData.schedulingRules
                      .inPersonSettings.serviceArea === 'fixed_zip_list' &&
                      selectedPackageForDetails.formData.schedulingRules
                        .inPersonSettings.zipCodes.length > 0 && (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.warning.main, 0.04),
                              borderColor: alpha(
                                theme.palette.warning.main,
                                0.2
                              ),
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontWeight: 600,
                                mb: 1.5,
                                display: 'block',
                              }}
                            >
                              Service ZIP Codes
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {selectedPackageForDetails.formData.schedulingRules.inPersonSettings.zipCodes.map(
                                (zip, idx) => (
                                  <Chip
                                    key={idx}
                                    label={zip}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(
                                        theme.palette.warning.main,
                                        0.12
                                      ),
                                      color: 'warning.main',
                                      fontWeight: 600,
                                      mb: 1,
                                    }}
                                  />
                                )
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      )}
                  </Grid>
                </Box>

                {/* Fees & Cancellation Policies */}
                {(selectedPackageForDetails.formData.schedulingRules
                  .travelOnsiteSurcharge != null ||
                  selectedPackageForDetails.formData.schedulingRules
                    .cancellationWindowHours != null ||
                  selectedPackageForDetails.formData.schedulingRules
                    .noShowFee != null ||
                  selectedPackageForDetails.formData.schedulingRules
                    .lateCancellationFee != null) && (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <PaymentIcon
                          sx={{ fontSize: 20, color: 'warning.main' }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          Fees & Cancellation Policies
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {selectedPackageForDetails.formData.schedulingRules
                          .travelOnsiteSurcharge != null && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  height: '100%',
                                  bgcolor: alpha(theme.palette.info.main, 0.04),
                                  borderColor: alpha(theme.palette.info.main, 0.2),
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                >
                                  Travel/Onsite Surcharge
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: 'info.main' }}
                                >
                                  $
                                  {selectedPackageForDetails.formData.schedulingRules.travelOnsiteSurcharge.toFixed(
                                    2
                                  )}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                        {selectedPackageForDetails.formData.schedulingRules
                          .cancellationWindowHours != null && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  height: '100%',
                                  bgcolor: alpha(
                                    theme.palette.secondary.main,
                                    0.04
                                  ),
                                  borderColor: alpha(
                                    theme.palette.secondary.main,
                                    0.2
                                  ),
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                  }}
                                >
                                  <TimeIcon
                                    sx={{ fontSize: 18, color: 'secondary.main' }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Cancellation Window
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: 'secondary.main' }}
                                >
                                  {
                                    selectedPackageForDetails.formData
                                      .schedulingRules.cancellationWindowHours
                                  }{' '}
                                  hrs
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                        {selectedPackageForDetails.formData.schedulingRules
                          .noShowFee != null && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  height: '100%',
                                  bgcolor: alpha(theme.palette.error.main, 0.04),
                                  borderColor: alpha(theme.palette.error.main, 0.2),
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                >
                                  No-Show Fee
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: 'error.main' }}
                                >
                                  $
                                  {selectedPackageForDetails.formData.schedulingRules.noShowFee.toFixed(
                                    2
                                  )}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}

                        {selectedPackageForDetails.formData.schedulingRules
                          .lateCancellationFee != null && (
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  height: '100%',
                                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                                  borderColor: alpha(
                                    theme.palette.warning.main,
                                    0.2
                                  ),
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                >
                                  Late Cancellation Fee
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: 'warning.main' }}
                                >
                                  $
                                  {selectedPackageForDetails.formData.schedulingRules.lateCancellationFee.toFixed(
                                    2
                                  )}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                      </Grid>
                    </Box>
                  )}

                {/* Documentation */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <ReportIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      Documentation Requirements
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    {/* Required Reports */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}
                      >
                        Required Reports
                      </Typography>
                      {selectedPackageForDetails.formData.documentation
                        .reportsRequired.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {reportTypes
                            .filter(r =>
                              selectedPackageForDetails.formData.documentation.reportsRequired.includes(
                                r.id
                              )
                            )
                            .map(report => (
                              <Chip
                                key={report.id}
                                label={report.name}
                                size="small"
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                  ),
                                  color: 'primary.main',
                                  fontWeight: 600,
                                  mb: 1,
                                }}
                              />
                            ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No specific reports required
                        </Typography>
                      )}
                    </Paper>

                    {/* Contract Attachments */}
                    {(selectedPackageForDetails.formData.documentation
                      .contractAttachments.length ?? 0) > 0 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.secondary.main, 0.04),
                            borderColor: alpha(theme.palette.secondary.main, 0.2),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <AttachmentIcon
                              sx={{ fontSize: 18, color: 'secondary.main' }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Attached Documents
                            </Typography>
                          </Box>
                          <Stack spacing={1}>
                            {(
                              selectedPackageForDetails.formData.documentation
                                .contractAttachments as Array<
                                  FileAttachment | string
                                >
                            ).map((attachment, idx) => {
                              const { name, url } =
                                getAttachmentDisplay(attachment)

                              return (
                                <Paper
                                  key={`${name}-${idx}`}
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    bgcolor: alpha(
                                      theme.palette.background.paper,
                                      0.6
                                    ),
                                    borderColor: alpha(
                                      theme.palette.secondary.main,
                                      0.2
                                    ),
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: alpha(
                                        theme.palette.secondary.main,
                                        0.08
                                      ),
                                      borderColor: alpha(
                                        theme.palette.secondary.main,
                                        0.4
                                      ),
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 2px 8px ${alpha(
                                        theme.palette.secondary.main,
                                        0.15
                                      )}`,
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 1,
                                        bgcolor: alpha(
                                          theme.palette.secondary.main,
                                          0.1
                                        ),
                                        color: 'secondary.main',
                                      }}
                                    >
                                      <DocumentIcon sx={{ fontSize: 20 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      {url ? (
                                        <Link
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          underline="none"
                                          sx={{
                                            display: 'block',
                                            fontWeight: 500,
                                            color: 'text.primary',
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                              color: 'secondary.main',
                                              textDecoration: 'underline',
                                            },
                                          }}
                                          title={name}
                                        >
                                          {name}
                                        </Link>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                          title={name}
                                        >
                                          {name}
                                        </Typography>
                                      )}
                                    </Box>
                                    {url && (
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          color: 'text.secondary',
                                          '&:hover': {
                                            color: 'secondary.main',
                                            bgcolor: alpha(
                                              theme.palette.secondary.main,
                                              0.1
                                            ),
                                          },
                                        }}
                                      >
                                        <OpenInNewIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    )}
                                  </Box>
                                </Paper>
                              )
                            })}
                          </Stack>
                        </Paper>
                      )}
                  </Stack>
                </Box>

                {/* Visibility & Matching */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <VisibilityIcon
                      sx={{ fontSize: 20, color: 'success.main' }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      Visibility & Matching
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    {/* Search Visibility */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: alpha(
                          selectedPackageForDetails.formData.visibilityMatching
                            .showInSearch
                            ? theme.palette.success.main
                            : theme.palette.warning.main,
                          0.04
                        ),
                        borderColor: alpha(
                          selectedPackageForDetails.formData.visibilityMatching
                            .showInSearch
                            ? theme.palette.success.main
                            : theme.palette.warning.main,
                          0.2
                        ),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        {selectedPackageForDetails.formData.visibilityMatching
                          .showInSearch ? (
                          <>
                            <CheckCircleIcon
                              sx={{ color: 'success.main', fontSize: 24 }}
                            />
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: 'success.main' }}
                              >
                                Visible in Patient Search
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Patients can discover and book this package
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <>
                            <CancelIcon
                              sx={{ color: 'warning.main', fontSize: 24 }}
                            />
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: 'warning.main' }}
                              >
                                Hidden from Patient Search
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Not visible to patients
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Paper>

                    {/* Body Regions */}
                    {selectedPackageForDetails.formData.visibilityMatching
                      .bodyRegions.length > 0 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.error.main, 0.04),
                            borderColor: alpha(theme.palette.error.main, 0.2),
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}
                          >
                            Body Regions
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {bodyRegions
                              .filter(r =>
                                selectedPackageForDetails.formData.visibilityMatching.bodyRegions.includes(
                                  r.id
                                )
                              )
                              .map(region => (
                                <Chip
                                  key={region.id}
                                  label={region.name}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.error.main,
                                      0.12
                                    ),
                                    color: 'error.main',
                                    fontWeight: 600,
                                    mb: 1,
                                  }}
                                />
                              ))}
                          </Stack>
                        </Paper>
                      )}

                    {/* Keywords/Tags */}
                    {selectedPackageForDetails.formData.visibilityMatching
                      .keywords.length > 0 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.info.main, 0.04),
                            borderColor: alpha(theme.palette.info.main, 0.2),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <TagIcon sx={{ fontSize: 18, color: 'info.main' }} />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Keywords & Tags
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {selectedPackageForDetails.formData.visibilityMatching.keywords.map(
                              (tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.info.main, 0.12),
                                    color: 'info.main',
                                    fontWeight: 600,
                                    mb: 1,
                                  }}
                                />
                              )
                            )}
                          </Stack>
                        </Paper>
                      )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Full package details are not available.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Button
            onClick={handleCloseDetailsDialog}
            variant="contained"
            sx={{ fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar?.open)}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
            sx={{ minWidth: 320 }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </>
  )
}

export default AgencyServicePackageList
